import {ServiceType} from '../../domain/types';
import * as React from 'react';
import { styled } from 'styled-components';
import { isEmpty, values } from 'ramda';
import {
  ListItem,
  RadioGroup,
  SpaceBetween,
  tk,
  Typography,
  Banner,
  Button,
  Icon
} from '@sunwebgroup/ui-components';

import { useIntl } from '@/intl';
import { useService } from '@/scopes/checkout/domain/hooks';

import ServiceBox from './ServiceBox';
import { updateBusService } from '../../domain/actions';
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable';
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints';
import { createAncillariesClickedEvent } from '../../domain/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags';
import { ParticipantName, Price, RoomName } from '@/scopes/checkout/components/services/ServiceComponents';

const Trailing = styled(ListItem.Trailing)`
  ${Button} {
    white-space: nowrap;
  }
`;
const Texts = styled(SpaceBetween)`
  flex: 1;
  ${Button} {
    width: fit-content;
  }
`;

export function BusService() {
  const intl = useIntl();
  const { service } = useService(ServiceType.Bus);

  useSendAncillaryAvailable(service, 'selectBus');

  if (!service) return null;
  const { outboundOptions } = service;

  if (!outboundOptions || values(outboundOptions).length === 0 ) return null;
  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_bus_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_bus_description' })}
      glyph="IconBusFront"
    >
      <BusServicePerParticipant />
    </ServiceBox>
  );
}

function BusServicePerParticipant() {
  const { service } = useService(ServiceType.Bus);

  if (!service) return null;

  const {rooms, participants } = service

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
      {rooms.map(({ id: roomId, name: roomName, participantIds }) => (
        <SpaceBetween key={roomId} direction="y" spacing={tk('spacing_md')}>
          {roomName ? <RoomName as="p" $variant="body-sm-bold">
            {roomName}
          </RoomName> : null}
          {participantIds.map((participantId) => {
            const participant = participants?.[participantId];
            if (!participant || participant.availableOptions.length === 0) {
              return null;
            }

            return (
              <ParticipantRow
                {...participant}
                key={participantId}
              />
            );
          })}
        </SpaceBetween>
      ))}
      {isEmpty(rooms) &&
        Object.values(participants ?? {}).map((participant) => (
          <ParticipantRow {...participant} key={participant.id} />
        ))}

    </SpaceBetween>
  );
}

function ParticipantRow({
  id,
  availableOptions,
  selectedValueOutbound,
  selectedValueNoneOption,
  name,
}: {
  id: string;
  availableOptions: string[];
  name: string;
  selectedValueOutbound: string | null;
  selectedValueNoneOption: string | null;
}) {
  const intl = useIntl();
  const { service, setServiceValue } = useService(ServiceType.Bus);
  const { sendEvent } = useAnalytics();
  const { participants, outboundOptions, noneOptions  } = service || {};

  const handleValueChange = React.useCallback(
    (value: string) => {
      if (!service) return;
      setServiceValue(updateBusService({ participantId: id, value, direction: 'outbound' })(service));
      sendEvent(
        createAncillariesClickedEvent({
          description: 'selectBusService',
          element: 'radiobutton',
          type: outboundOptions ? outboundOptions[value ?? '']?.label ?? '' : '',
          result: outboundOptions ? outboundOptions[value ?? '']?.price ?? 0 : 0
        })
      )
    },
    [sendEvent, setServiceValue, service, outboundOptions, id]
  );

  if (!service) return null;
  if (!participants) return null;
  return (
    <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
      {values(participants).length > 1 && (
        <ParticipantName as="span" $variant="body-xs">
          {name}
        </ParticipantName>
      )}
      <SpaceBetween direction='y' spacing={tk('spacing_md')}>
        <RadioGroup.Root value={selectedValueOutbound ?? selectedValueNoneOption ?? null} onValueChange={handleValueChange}>
          <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
            {(outboundOptions && values(outboundOptions).length > 0) && (
              (() => {
                const { id: optionId, badge, price } = selectedValueOutbound && noneOptions && !noneOptions[selectedValueOutbound] ? outboundOptions[selectedValueOutbound ?? ''] : values(outboundOptions)[0];
                return (
                  <ListItem.Root key={optionId}>
                    <ListItem.Control>
                      <RadioGroup.Item value={optionId} />
                    </ListItem.Control>
                    <ListItem.Content>
                      {badge && <ListItem.Badge $state="success">{badge}</ListItem.Badge>}
                      <ListItem.Label>{intl.formatMessage({id: 'checkout_steps_ancillaries_bus_yes_label'}) }</ListItem.Label>
                    </ListItem.Content>
                    <ListItem.Trailing>
                      <Price as="span" $variant="label">
                        {intl.formatCurrency(price)}
                      </Price>
                    </ListItem.Trailing>
                  </ListItem.Root>
                )
              })()
            )}

            {availableOptions.map((optionId) => {
              if ( noneOptions && noneOptions[optionId]) {
                const { id: noneOptionId, badge, price } = noneOptions[optionId];
                return (
                  <ListItem.Root key={noneOptionId}>
                    <ListItem.Control>
                      <RadioGroup.Item value={noneOptionId} />
                    </ListItem.Control>
                    <ListItem.Content>
                      {badge && <ListItem.Badge $state="success">{badge}</ListItem.Badge>}
                      <ListItem.Label>{intl.formatMessage({id: 'checkout_steps_ancillaries_bus_no_label'}) }</ListItem.Label>
                    </ListItem.Content>
                    <ListItem.Trailing>
                      <Price as="span" $variant="label">
                        {intl.formatCurrency(price, true)}
                      </Price>
                    </ListItem.Trailing>
                  </ListItem.Root>
                );
              }
              return null;
            })}
          </SpaceBetween>
        </RadioGroup.Root>

        {selectedValueOutbound && noneOptions && !noneOptions[selectedValueOutbound]  && <BusPickUpPoints participantId={id} />}

      </SpaceBetween>

    </SpaceBetween>
  );
}

function BusPickUpPoints({
                           participantId
                         }: {
  participantId: string;
}) {
  const intl = useIntl();
  const { service } = useService(ServiceType.Bus);

  const onClickHandler = (url: string) => {
    window.open(url, '_blank');
  };

  const showMoreThanOnePickUpPoint = useFeatureFlag('SHOW_GROUPS_BUS_MORE_THAN_ONE_PICKUP_POINT');
  const pickUpPointKnown = useFeatureFlag('SHOW_GROUPS_BUS_PICKUP_POINT_KNOWN');

  if (!service) return null;
  const { participants, outboundOptions } = service;

  const moreThanOnePickUpPoint = showMoreThanOnePickUpPoint && outboundOptions && values(outboundOptions).length > 1;
  const pickUpPoint = participants[participantId].selectedValueOutbound;
  const pickUpPointOption = pickUpPoint ? outboundOptions[pickUpPoint] : null;

  return (
    <>
      {moreThanOnePickUpPoint && <MoreThanOnePickUpPoint participantId={participantId}/> }
      {!moreThanOnePickUpPoint && (
        <>
          <Typography $variant="body-lg-bold">{intl.formatMessage({id: 'checkout_steps_ancillaries_bus_pick_up_and_return_point' })}</Typography>
          <Banner $type={'neutral-dark'} >
            <Icon glyph="IconPinMap" size="lg" />
            <Texts direction="y">
              {pickUpPointKnown ? (
                <>
                  <Typography $variant="body-md-bold">{pickUpPointOption?.label}</Typography>
                  <Button $withoutColor $hierarchy="primary" $size="small" onClick={() => onClickHandler(`https://www.google.com/maps/search/?q=${pickUpPointOption?.label}`)}>
                    {intl.formatMessage({id: 'checkout_steps_ancillaries_bus_show_on_map_text'})}
                  </Button>
                </>
              ) : (
                <>
                  <Typography $variant="body-md-bold">{intl.formatMessage({id: 'checkout_steps_ancillaries_bus_pick_up_not_know_title'})}</Typography>
                  <Typography $variant="body-md">{intl.formatMessage({id: 'checkout_steps_ancillaries_bus_pick_up_not_know_description'})}</Typography>
                </>)
              }
            </Texts>
          </Banner>
        </>
      )}
    </>
  )
}

function MoreThanOnePickUpPoint({
                                  participantId
                                }: {
  participantId: string;
}) {
  const { service, setServiceValue } = useService(ServiceType.Bus);
  const { isDesktopSm } = useBreakpoints();
  const { sendEvent } = useAnalytics();
  const intl = useIntl();

  const { participants, outboundOptions } = service ?? {};

  const handleOutboundValueChange = React.useCallback(
    (value: string) => {
      if (!service) return;
      setServiceValue(updateBusService({ participantId, value, direction: 'outbound' })(service));
      sendEvent(
        createAncillariesClickedEvent({
          description: 'selectBusService',
          element: 'radiobutton',
          type: outboundOptions ? outboundOptions[value ?? '']?.label ?? '' : '',
          result: outboundOptions ? outboundOptions[value ?? '']?.price ?? 0 : 0
        })
      )
    },
    [sendEvent, setServiceValue, service, outboundOptions, participantId]
  );

  const onClickHandler = (url: string) => {
    window.open(url, '_blank');
  };

  if (!service) return null;
  if (!participants) return null;
  const participant = participants[participantId];

  const {availableOptions, selectedValueOutbound } = participant;

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
      {outboundOptions && values(outboundOptions).length > 0 && (
        <>
          <Typography $variant="body-md-bold">{intl.formatMessage({id: 'checkout_steps_ancillaries_bus_pick_up_and_return_point'})}</Typography>
          <RadioGroup.Root value={selectedValueOutbound ?? null} onValueChange={handleOutboundValueChange}>
            <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
              { outboundOptions && values(outboundOptions).map(({ id, label }) => {
                if (!availableOptions.includes(id)) {
                  return
                }
                return (
                  <ListItem.Root key={id}>
                    <ListItem.Control>
                      <RadioGroup.Item value={id} />
                    </ListItem.Control>
                    <ListItem.Content>
                      <ListItem.Label>{label}</ListItem.Label>
                    </ListItem.Content>
                    <Trailing>
                      <Button $withoutColor $hierarchy="primary" $size="small" onClick={() => onClickHandler(`https://www.google.com/maps/search/?q=${label}`)}>
                        <>
                          {isDesktopSm ? <Icon glyph="IconMap" size="md" /> : <Icon glyph="IconPinMap" size="md" /> }
                          {isDesktopSm ? <Typography>{intl.formatMessage({id: 'checkout_steps_ancillaries_bus_show_on_map_text'})}</Typography> : ''}
                        </>
                      </Button>
                    </Trailing>
                  </ListItem.Root>
                )}
              )}
            </SpaceBetween>
          </RadioGroup.Root>
        </>
      )}
    </SpaceBetween>

  )
}
